import { useReportingStore } from "../../../../../stores/ReportingStore";
import LoaderStats from "../../LoaderStats";
import {
  ColorSwatch,
  Group,
  NumberFormatter,
  Stack,
  Text,
} from "@mantine/core";
import { DonutChart } from "@mantine/charts";
import { useMemo, useState } from "react";
import { getChartColors } from "../../../../../functions/utilFunctions";
import CustomActiveShape from "../../reportingEmail/perf/CustomActiveShape";
import { useLanguage } from "../../../../../stores/LanguageStore";

const PerfLinkedinRate = ({ type }) => {
  const lang = useLanguage((s) => s.language);
  const dateRange = useReportingStore((s) => s.dateRange);
  const actualStats = useReportingStore((s) => s.actualStats);
  const actualStatsDate = useReportingStore((s) => s.actualStatsDate);
  const [hoveredIndex, setHoveredIndex] = useState(0);

  const handleHover = (_, index) => {
    setHoveredIndex(index);
  };

  const statistics = useMemo(() => {
    if (!dateRange.start_date && !dateRange.end_date) {
      return actualStats;
    } else {
      const totals = actualStatsDate.reduce((acc, curr) => {
        Object.keys(curr).forEach((key) => {
          if (key !== "date") {
            acc[key] = (acc[key] || 0) + curr[key];
          }
        });
        return acc;
      }, {});

      return {
        opener_total: totals.opener,
        clicker_total: totals.clicker,
        answerer_total: totals.answerer,
        contact_email_bounce: totals.bounce,
        ...totals,
      };
    }
  }, [actualStatsDate, dateRange.end_date, dateRange.start_date, actualStats]);

  const acceptationRate = useMemo(() => {
    if (statistics.linkedin_invitation_sent) {
      return (
        (statistics.linkedin_invitation_accepted /
          statistics.linkedin_invitation_sent) *
        100
      );
    }
    return 0;
  }, [
    statistics.linkedin_invitation_accepted,
    statistics.linkedin_invitation_sent,
  ]);

  const restRate = useMemo(() => {
    let rest =
      statistics.linkedin_invitation_sent -
      statistics.linkedin_invitation_accepted;
    if (statistics.linkedin_invitation_sent) {
      return (rest / statistics.linkedin_invitation_sent) * 100;
    }
    return 0;
  }, [
    statistics.linkedin_invitation_accepted,
    statistics.linkedin_invitation_sent,
  ]);

  const data = useMemo(
    () => [
      {
        name: lang.reports.accepted,
        value: Math.round(acceptationRate * 10) / 10,
        full: statistics.linkedin_invitation_accepted,
        color: getChartColors()[2],
      },
      {
        name: lang.reports.ignored,
        value: Math.round(restRate * 10) / 10,
        full:
          statistics.linkedin_invitation_sent -
          statistics.linkedin_invitation_accepted,
        color: "red",
      },
    ],
    [
      acceptationRate,
      restRate,
      statistics.linkedin_invitation_accepted,
      statistics.linkedin_invitation_sent,
      lang,
    ]
  );

  return (
    <Stack pos={"relative"} flex={1} align="center">
      <LoaderStats isDate />

      <Group mb={16} justify="space-between" w={"100%"} px={"md"}>
        <Text size="lg" fw={600} c={"var(--mantine-color-primary-6)"}>
          {lang.reports.engagement}
        </Text>
        <span style={{ color: "black", fontSize: 12, marginLeft: "auto" }}>
          <b>
            <NumberFormatter value={statistics.linkedin_invitation_sent} />
          </b>
        </span>
      </Group>

      <Group justify="center">
        <DonutChart
          w={"100%"}
          h={250}
          data={data}
          thickness={32}
          withTooltip={false}
          paddingAngle={3}
          pieProps={{
            isAnimationActive: true,
            animationDuration: 600,
            activeShape: (props) => <CustomActiveShape {...props} />,
            activeIndex: hoveredIndex,
            onMouseEnter: handleHover,
          }}
        />

        <Group gap={"md"} justify="center" w={"100%"}>
          <Group gap={4}>
            <ColorSwatch
              color={`var(--mantine-color-${getChartColors()[2]}-5)`}
              size={16}
            />
            <Text size="sm">
              {lang.reports.invitations} {lang.reports.accepted}
            </Text>
          </Group>
          <Group gap={4}>
            <ColorSwatch color={`var(--mantine-color-red-5)`} size={16} />
            <Text size="sm">
              {lang.reports.invitations} {lang.reports.ignored}
            </Text>
          </Group>
        </Group>
      </Group>
    </Stack>
  );
};

export default PerfLinkedinRate;
